/**
 * Jul-2026 (User-Wunsch) — „Artikel checken"-QR auf der Warenwirtschaft-Seite.
 * Scan mit dem Handy öffnet das login-freie Check-Portal (/artikel-check/{token}):
 * Barcode scannen/suchen → alle Artikeldaten inkl. Bestand ALLER Standorte.
 * Jul-2026 (User-Wunsch): Klick = vergrößern + ausdruckbar (wie Handy-Wareneingang).
 */
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ScanSearch, Printer, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { openQrPrint, dealerPrintInfo } from "../../lib/qrPrint";
import { QrLabelSheetButton } from "../QrLabelSheetButton";

export default function ArticleCheckQr() {
    const { user } = useAuth();
    const [session, setSession] = useState(null);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        api.post("/articles/check-sessions").then((r) => setSession(r.data)).catch(() => setSession(null));
    }, []);
    if (!session?.portal_url) return null;

    const handlePrint = async () => {
        const ok = await openQrPrint({
            title: "Artikel checken per Handy",
            subtitle: "Mit der Handy-Kamera scannen — Barcode scannen oder Artikel suchen, zeigt alle Artikeldaten inkl. Bestand aller Standorte (ohne Anmeldung).",
            scanUrl: session.portal_url,
            ...dealerPrintInfo(user),
        });
        if (!ok) toast.error("Popup blockiert — bitte Pop-ups erlauben");
    };

    return (
        <div className="relative shrink-0">
            <button type="button" onClick={() => setOpen((o) => !o)}
                className="flex flex-col items-center bg-white rounded-sm p-1.5 border border-gray-600 hover:ring-2 hover:ring-[#FFCC00] transition-shadow"
                data-testid="wms-article-check-qr" title="Handy-Scan · Bestand aller Standorte (Klick: vergrößern & drucken)">
                <QRCodeSVG value={session.portal_url} size={64} level="M" />
                <div className="mt-1 text-[9px] uppercase tracking-wider font-black text-[#111827] inline-flex items-center gap-1">
                    <ScanSearch className="w-3 h-3" /> Artikel checken
                </div>
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-2 z-50 w-72 bg-white border border-gray-200 rounded-sm shadow-xl p-4 text-[#111827]" data-testid="wms-article-check-qr-panel">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Artikel checken per Handy</span>
                        <button type="button" onClick={() => setOpen(false)} data-testid="wms-article-check-qr-close"><X className="w-4 h-4 text-gray-500" /></button>
                    </div>
                    <div className="flex justify-center bg-white p-2 rounded-sm border-2 border-[#FFCC00]">
                        <QRCodeSVG value={session.portal_url} size={180} level="M" />
                    </div>
                    <p className="text-[11px] text-gray-600 mt-2">Mit dem Handy scannen: Barcode scannen oder Artikel suchen — zeigt alle Artikeldaten inkl. Bestand <b>aller Standorte</b> (ohne Anmeldung).</p>
                    <button type="button" onClick={handlePrint}
                        className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#111827] text-white rounded-sm py-2 text-xs font-bold uppercase tracking-wider hover:bg-black"
                        data-testid="wms-article-check-qr-print">
                        <Printer className="w-4 h-4" /> QR-Code drucken
                    </button>
                    <QrLabelSheetButton scanUrl={session.portal_url}
                        label="Artikel checken per Handy — QR scannen"
                        testidPrefix="wms-article-check-qr-label" />
                </div>
            )}
        </div>
    );
}
